import { useState, useRef, useCallback, useEffect } from 'react';

/**
 * Custom hook wrapping the Web Speech API for speech-to-text.
 *
 * Returns:
 *   isListening  — boolean, true while actively recording
 *   transcript   — string, the latest finalized + interim transcript segment
 *   isSupported  — boolean, true if the browser supports SpeechRecognition
 *   startListening — function, begins speech capture
 *   stopListening  — function, ends speech capture
 *   error        — string|null, last error message
 */
export default function useSpeechRecognition() {
  const SpeechRecognition =
    typeof window !== 'undefined'
      ? window.SpeechRecognition || window.webkitSpeechRecognition
      : null;

  const isSupported = Boolean(SpeechRecognition);

  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [error, setError] = useState(null);
  const recognitionRef = useRef(null);
  const errorTimeoutRef = useRef(null);

  const setErrorWithAutoDismiss = useCallback((msg) => {
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
      errorTimeoutRef.current = null;
    }
    setError(msg);
    if (msg) {
      errorTimeoutRef.current = setTimeout(() => {
        setError(null);
        errorTimeoutRef.current = null;
      }, 5000);
    }
  }, []);

  const startListening = useCallback(() => {
    if (!isSupported) {
      setErrorWithAutoDismiss('Speech recognition is not supported in this browser.');
      return;
    }

    setError(null);
    setTranscript('');

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    recognition.onstart = () => {
      setIsListening(true);
    };

    recognition.onresult = (event) => {
      let finalText = '';
      let interimText = '';
      for (let i = 0; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          finalText += result[0].transcript;
        } else {
          interimText += result[0].transcript;
        }
      }
      setTranscript(finalText + interimText);
    };

    recognition.onerror = (event) => {
      // 'no-speech' and 'aborted' are not real failures — the user just
      // stayed silent or stopped recording.  Surface everything else.
      if (event.error !== 'no-speech' && event.error !== 'aborted') {
        setErrorWithAutoDismiss(`Speech recognition error: ${event.error}`);
      }
      setIsListening(false);
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognitionRef.current = recognition;
    recognition.start();
  }, [isSupported, SpeechRecognition, setErrorWithAutoDismiss]);

  const stopListening = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    setIsListening(false);
  }, []);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
        recognitionRef.current = null;
      }
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
        errorTimeoutRef.current = null;
      }
    };
  }, []);

  return { isListening, transcript, isSupported, startListening, stopListening, error };
}
